import { useEffect, useState } from 'react';
import type { FlowNode, FlowData, TriggerData, WaitData, ConditionData, ActionData, AiSmartData } from '../types';
import { fetchModels, type AiModel } from '../lib/aiBuilder';
import { MergeTagInput } from './MergeTagInput';

interface Props {
  node: FlowNode | null;
  onUpdate: (id: string, data: FlowData) => void;
  onDelete: (id: string) => void;
}

const TRIGGER_SOURCES = [
  { value: 'call.missed',        label: 'Inbound call missed' },
  { value: 'call.completed',     label: 'Inbound call completed' },
  { value: 'sms.received',       label: 'SMS received' },
  { value: 'job.status_changed', label: 'Job status changed' },
  { value: 'invoice.aged',       label: 'Invoice aged · unpaid' },
  { value: 'storm.hail_cell',    label: 'Storm hail cell in zip' },
  { value: 'lead.created',       label: 'Lead created' },
  { value: 'schedule.cron',      label: 'Scheduled · cron' },
];

const CONDITION_FIELDS = [
  { value: 'customer.last_serviced_months', label: 'customer.last_serviced_months' },
  { value: 'customer.sms_opt_in',           label: 'customer.sms_opt_in' },
  { value: 'customer.tag',                  label: 'customer.tag' },
  { value: 'invoice.balance_due',           label: 'invoice.balance_due' },
  { value: 'invoice.age_days',              label: 'invoice.age_days' },
  { value: 'storm.cell_size_in',            label: 'storm.cell_size_in' },
  { value: 'job.status',                    label: 'job.status' },
];

const OPERATORS = [
  { value: 'equals',      label: 'equals' },
  { value: 'not_equals',  label: 'not equals' },
  { value: 'gt',          label: 'is more than' },
  { value: 'lt',          label: 'is less than' },
  { value: 'gte',         label: 'is at least' },
  { value: 'lte',         label: 'is at most' },
  { value: 'contains',    label: 'contains' },
  { value: 'starts_with', label: 'starts with' },
];

const ACTION_TYPES = [
  { value: 'sms.send',            label: 'Send SMS' },
  { value: 'email.send',          label: 'Send email' },
  { value: 'task.create',         label: 'Create task' },
  { value: 'workflow.notify_rep', label: 'Notify rep' },
  { value: 'webhook.post',        label: 'POST webhook' },
  { value: 'log.audit',           label: 'Log to audit only' },
];

const WAIT_UNITS = ['seconds', 'minutes', 'hours', 'days'] as const;

export function PropertiesPanel({ node, onUpdate, onDelete }: Props) {
  if (!node) {
    return (
      <aside className="props" data-tour="props">
        <div className="props-head">
          <b>Properties</b>
          <span>Click a node to edit</span>
        </div>
        <div className="props-empty">
          <span className="icon">◆</span>
          <span>Click any node on the canvas to edit its properties here. Drag a new node from the left palette to add one.</span>
        </div>
        <div className="props-foot">
          <span className="merge-help">
            Use <code>{'{{customer.phone}}'}</code> or <code>{'{{invoice.balance}}'}</code> as merge tags inside SMS / email bodies.
          </span>
        </div>
      </aside>
    );
  }

  const d = node.data as FlowData;

  function patch<T extends FlowData>(updates: Partial<T>) {
    onUpdate(node!.id, { ...node!.data, ...updates } as FlowData);
  }

  return (
    <aside className="props" data-tour="props">
      <div className="props-head">
        <b>{d.kind} · properties</b>
        <span>{node.id}</span>
      </div>
      <div className="props-body">

        <div className="props-field">
          <label>Label</label>
          <input value={d.label} onChange={e => patch({ label: e.target.value })} />
          <span className="help">Shown on the node card.</span>
        </div>

        {d.kind === 'trigger' && (
          <>
            <div className="props-field">
              <label>Source</label>
              <select
                value={(d as TriggerData).source}
                onChange={e => patch<TriggerData>({ source: e.target.value as TriggerData['source'] })}
              >
                {TRIGGER_SOURCES.map(s => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </select>
            </div>
            <div className="props-field">
              <label>Notes</label>
              <textarea
                rows={3}
                value={d.description || ''}
                onChange={e => patch({ description: e.target.value })}
              />
            </div>
          </>
        )}

        {d.kind === 'wait' && (
          <>
            <div className="props-row">
              <div className="props-field">
                <label>Amount</label>
                <input
                  type="number"
                  min={0}
                  value={(d as WaitData).amount}
                  onChange={e => patch<WaitData>({ amount: parseInt(e.target.value, 10) || 0 })}
                />
              </div>
              <div className="props-field">
                <label>Unit</label>
                <select
                  value={(d as WaitData).unit}
                  onChange={e => patch<WaitData>({ unit: e.target.value as WaitData['unit'] })}
                >
                  {WAIT_UNITS.map(u => <option key={u} value={u}>{u}</option>)}
                </select>
              </div>
            </div>
            <div className="props-field">
              <label>Notes</label>
              <textarea
                rows={3}
                value={d.description || ''}
                onChange={e => patch({ description: e.target.value })}
              />
            </div>
          </>
        )}

        {d.kind === 'condition' && (
          <>
            <div className="props-field">
              <label>Field to check</label>
              <select
                value={(d as ConditionData).field}
                onChange={e => patch<ConditionData>({ field: e.target.value as ConditionData['field'] })}
              >
                {CONDITION_FIELDS.map(f => (
                  <option key={f.value} value={f.value}>{f.label}</option>
                ))}
              </select>
            </div>
            <div className="props-row">
              <div className="props-field">
                <label>Operator</label>
                <select
                  value={(d as ConditionData).operator}
                  onChange={e => patch<ConditionData>({ operator: e.target.value as ConditionData['operator'] })}
                >
                  {OPERATORS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
              <div className="props-field">
                <label>Value</label>
                <input
                  value={(d as ConditionData).value}
                  onChange={e => patch<ConditionData>({ value: e.target.value })}
                />
              </div>
            </div>
            <div className="props-field">
              <span className="help">Condition has 2 outputs · drag from the <b style={{ color: 'var(--signal-green)' }}>left handle</b> for match, <b style={{ color: 'var(--quiet)' }}>right handle</b> for no-match.</span>
            </div>
          </>
        )}

        {d.kind === 'ai_smart' && <AiSmartFields d={d as AiSmartData} patch={patch} />}

        {d.kind === 'action' && (
          <>
            <div className="props-field">
              <label>Action type</label>
              <select
                value={(d as ActionData).type}
                onChange={e => patch<ActionData>({ type: e.target.value as ActionData['type'] })}
              >
                {ACTION_TYPES.map(a => (
                  <option key={a.value} value={a.value}>{a.label}</option>
                ))}
              </select>
            </div>
            <div className="props-field">
              <label>To (phone / email / endpoint)</label>
              <input
                value={(d as ActionData).to || ''}
                onChange={e => patch<ActionData>({ to: e.target.value })}
                placeholder="{{customer.phone}}"
              />
            </div>
            <div className="props-field">
              <label>Body / payload</label>
              <MergeTagInput
                rows={4}
                value={(d as ActionData).body || ''}
                onChange={val => patch<ActionData>({ body: val })}
                placeholder="Heavy hail in your zip last night. Free inspection: {{schedule_link}}"
              />
              <span className="help">Type <code style={{ background: 'rgba(195,163,91,0.14)', color: 'var(--gold-light)', padding: '0 4px', borderRadius: 3 }}>{'{{'}</code> to autocomplete merge tags.</span>
            </div>
          </>
        )}

      </div>
      <div className="props-foot">
        <span className="merge-help">
          <code>{'{{customer.phone}}'}</code> · <code>{'{{invoice.balance}}'}</code> · <code>{'{{storm.cell_size}}'}</code>
        </span>
        <button className="toolbar-btn danger" onClick={() => onDelete(node.id)}>Delete node</button>
      </div>
    </aside>
  );
}

function AiSmartFields({ d, patch }: { d: AiSmartData; patch: (u: Partial<AiSmartData>) => void }) {
  const [models, setModels] = useState<AiModel[]>([]);
  useEffect(() => { fetchModels().then(setModels); }, []);

  return (
    <>
      <div className="props-field">
        <label>Model</label>
        <select value={d.model} onChange={e => patch({ model: e.target.value })}>
          {(models.length ? models : [{ id: d.model, label: 'Llama 3.1 8B', desc: '' }]).map(m => (
            <option key={m.id} value={m.id}>{m.label}</option>
          ))}
        </select>
        <span className="help">Free models · runs on Cloudflare Workers AI.</span>
      </div>
      <div className="props-field">
        <label>Prompt</label>
        <MergeTagInput
          rows={6}
          value={d.prompt}
          onChange={val => patch({ prompt: val })}
          placeholder="Summarize the customer's recent activity in 2 sentences."
        />
        <span className="help">Type <code style={{ background: 'rgba(195,163,91,0.14)', color: 'var(--gold-light)', padding: '0 4px', borderRadius: 3 }}>{'{{'}</code> to autocomplete merge tags.</span>
      </div>
      <div className="props-row">
        <div className="props-field">
          <label>Output format</label>
          <select value={d.format} onChange={e => patch({ format: e.target.value as 'text' | 'json' })}>
            <option value="text">Plain text</option>
            <option value="json">Structured JSON</option>
          </select>
        </div>
        <div className="props-field">
          <label>Store output as</label>
          <input value={d.storeAs || ''} onChange={e => patch({ storeAs: e.target.value })} placeholder="ai_summary" />
        </div>
      </div>
    </>
  );
}
